import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogOpenEvent.eventName, this.#injectProgressBar);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogOpenEvent.eventName, this.#injectProgressBar);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #injectProgressBar = () => {
    const THRESHOLD = 20000; // 200 zł w groszach

    function fmt(c) {
      return (c / 100).toFixed(2).replace('.', ',') + ' zł';
    }

    const dialog = this.refs?.dialog;
    if (!dialog) return;

    // Usuń stary pasek jeśli istnieje
    dialog.querySelector('.cart-progress-bar')?.remove();

    fetch('/cart.js')
      .then(r => r.json())
      .then(cart => {
        const header = dialog.querySelector('.cart-drawer__header');
        if (!header) return;

        const total = cart.total_price;
        const rem = Math.max(THRESHOLD - total, 0);
        const pct = Math.min((total / THRESHOLD) * 100, 100);

        const bar = document.createElement('div');
        bar.className = 'cart-progress-bar';
        bar.style.cssText = 'background:#111;padding:10px 16px;border-bottom:1px solid #222;';

        if (rem <= 0) {
          bar.innerHTML = `
            <div style="color:#00cc44;font-size:.8rem;font-weight:700;text-align:center;margin-bottom:6px;">
              🎉 Masz darmową dostawę!
            </div>
            <div style="background:#333;border-radius:4px;height:6px;overflow:hidden;">
              <div style="background:#00cc44;width:100%;height:100%;border-radius:4px;"></div>
            </div>`;
        } else {
          bar.innerHTML = `
            <div style="color:#fff;font-size:.8rem;font-weight:700;text-align:center;margin-bottom:6px;">
              Brakuje Ci jeszcze <span style="color:#ff2222;font-weight:900;">${fmt(rem)}</span> do <strong>DARMOWEJ DOSTAWY</strong> 🚚
            </div>
            <div style="background:#333;border-radius:4px;height:6px;overflow:hidden;">
              <div style="background:#ff2222;width:${pct}%;height:100%;border-radius:4px;transition:width .4s;"></div>
            </div>`;
        }

        header.insertAdjacentElement('afterend', bar);
      })
      .catch(() => {});
  };

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
